import { DeleteOutlined } from '@ant-design/icons';
import {
  AutoComplete,
  Button, Flex, Select, Typography,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import {
  type ComparatorUnits, type CompareAsUnits, getComparatorOptionsForUnit, getCompareAsOptions,
} from 'automations/data/comapreNode';
import useMailMergeFields from 'automations/hooks/useMailMergeFields';
import { type CompareNodeData } from 'automations/types/nodeTypes';
import clsx from 'clsx';
import { filterMailMergeOptions, getMailMergeValue } from 'crm-atoms/utils';
import { useCallback } from 'react';

import ComparatorRenderer from '../ComparatorRenderer';
import styles from './CompareNodeMenu.module.scss';

type Comparator = CompareNodeData['additionalOptions']['compare']['comparator'];
type Criteria = CompareNodeData['additionalOptions']['compare']['criterias'][number];

function AutoCompleteInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { options } = useMailMergeFields({});

  const showOptions = value.includes('@');
  const searchString = value.slice(value.lastIndexOf('@') + 1);
  const filteredOptions = filterMailMergeOptions(options, searchString);

  const handleSelect = useCallback((newValue: string) => {
    onChange(value.slice(0, value.lastIndexOf('@')) + getMailMergeValue(newValue));
  }, [onChange, value]);

  return (
    <AutoComplete
      options={showOptions ? filteredOptions : []}
      value={value}
      onChange={onChange}
      onSelect={handleSelect}
    >
      <TextArea
        placeholder="Type @ to add a mail merge field"
        rows={3}
        className={styles.textArea}
      />
    </AutoComplete>
  );
}

export default function CompareCriteriaBuilder({
  criteria,
  currentIndex,
  onChange,
  onDelete,
  comparator,
}: {
  criteria: Criteria;
  currentIndex: number;
  onChange: (criteria: Criteria) => void;
  onDelete: () => void;
  comparator: Comparator;
}) {
  const handleCompareAsChange = useCallback((value: CompareAsUnits) => {
    const newOptions = getComparatorOptionsForUnit(value);
    onChange({
      ...criteria,
      compareAs: value,
      comparator: newOptions[0]?.value as ComparatorUnits,
    });
  }, [criteria, onChange]);

  const handleComparatorChange = useCallback((value: ComparatorUnits) => {
    onChange({
      ...criteria,
      comparator: value,
    });
  }, [criteria, onChange]);

  const isFirstCriteria = currentIndex === 0;

  const handleConditionKeyChange = useCallback((value: string) => {
    onChange({
      ...criteria,
      conditionKey: value,
    });
  }, [criteria, onChange]);

  const handleConditionValueChange = useCallback((value: string | undefined) => {
    onChange({
      ...criteria,
      conditionValue: value,
    });
  }, [criteria, onChange]);

  return (
    <Flex gap={16} justify="space-between">
      {isFirstCriteria ? (
        <Typography.Title level={5} className={styles.when}>
          When
        </Typography.Title>
      ) : (
        <ComparatorRenderer comparator={comparator} />
      )}
      <Flex gap={16} className={styles.contentContainer}>
        <AutoCompleteInput
          value={criteria.conditionKey}
          onChange={handleConditionKeyChange}
        />
        <Flex vertical gap={4}>
          <Select
            className={styles.select}
            options={getCompareAsOptions}
            value={criteria.compareAs}
            onChange={handleCompareAsChange}
          />
          <Select
            options={getComparatorOptionsForUnit(criteria.compareAs)}
            value={criteria.comparator}
            className={styles.select}
            onChange={handleComparatorChange}
          />
        </Flex>
        <AutoCompleteInput
          value={criteria.conditionValue ?? ''}
          onChange={handleConditionValueChange}
        />
      </Flex>
      <Button
        type="link"
        size="small"
        icon={<DeleteOutlined />}
        danger
        className={clsx(styles.deleteButton, isFirstCriteria && styles.hideBtn)}
        onClick={isFirstCriteria ? () => {} : onDelete}
      >
        Remove
      </Button>
    </Flex>
  );
}
